import React, { useEffect, useLayoutEffect, useState, useContext } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabaseClient';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import CadastroTalhao from './CadastroTalhao';
import TalhaoPlantios from './TalhaoPlantios';
import { getPlotPlantingView } from './plotPlantingView';
import { usePlanLimits } from '../hooks/usePlanLimits';
import UpgradeModal from '../components/UpgradeModal';
import GestaoCultivoModal from '../components/GestaoCultivoModal';
import { useToast } from '../components/Toast';
import { ThemeContext } from '../App';
import { aplicarContextoCultivoAoTalhao, buscarContextosCultivo, contextoCultivoLegado } from '../services/ciclosCultivoService';
import { operationalPlantingToLegacyPlot } from '../services/operationalPropertiesService';
import { deleteOperationalPlanting, deleteOperationalPlot, updateOperationalPlot } from '../services/plotMigrationReviewService';
import { deleteEmptyLegacyPlot } from '../services/legacyPlotDeletionService';
import { motivoBloqueioExclusaoTalhao, removerPlantioDoTalhaoOperacional } from '../services/plotDeletionRules';
import { resetPageScroll } from '../utils/pageScroll';
import { summarizeCropLifecycles } from '../utils/cropLifecycle';
import { propertyActionAllowed } from '../utils/collaborationAccess';
import { checkSharedPlotCapacity } from '../services/collaborationService';
import { pecuariaPlanting } from '../pecuaria/pecuariaDomain';
import { FaArrowLeft, FaChevronRight, FaEdit, FaFilter, FaHistory, FaLayerGroup, FaLeaf, FaMapMarkedAlt, FaPlus, FaRulerCombined, FaSearch, FaSeedling, FaTrash } from 'react-icons/fa';
import './propriedades-platform.css';

const formatHistoricalDate = value => {
  if (!value) return 'Data não informada';
  const [year, month, day] = String(value).split('-');
  return year && month && day ? `${day}/${month}/${year}` : String(value);
};

export default function ListaTalhoes({ propriedade, initialTalhoes = [], operationalMode = false, onSelecionar, onVoltar, onNavigate, onTalhoesChange, user, permission = 'owner', initialPlotId = null, initialPlotName = '', initialSection = null, returnTarget = null, returnContext = null }) {
  const [talhoes, setTalhoes] = useState(() => operationalMode ? initialTalhoes : []);
  const isOnline = useNetworkStatus();
  const toast = useToast();
  const { darkMode } = useContext(ThemeContext);
  const [editTalhao, setEditTalhao] = useState(null);
  const [gestaoCultivoTalhao, setGestaoCultivoTalhao] = useState(null);
  const [gestaoCultivoModo, setGestaoCultivoModo] = useState('auto');
  const [aba, setAba] = useState('listar'); // Agora começa na aba de listagem
  const [talhaoSelecionado, setTalhaoSelecionado] = useState(null);

  useLayoutEffect(() => {
    resetPageScroll();
  }, [talhaoSelecionado?.id]);
  const [confirmarExclusao, setConfirmarExclusao] = useState({ aberto: false, tipo: null, id: null, nome: '', talhaoId: null, erro: '' });
  const [excluindo, setExcluindo] = useState(false);
  
  // Sistema de Planos
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limiteInfo, setLimiteInfo] = useState(null);
  const [salvandoTalhao, setSalvandoTalhao] = useState(false);
  const { verificarLimite, plano } = usePlanLimits(user?.id);
  
  // Permissão: 'owner' e 'editor' podem editar, 'viewer' só visualiza
  const podeEditar = propertyActionAllowed(propriedade, 'propriedades', 'edit', permission === 'owner' || permission === 'editor');
  const podeExcluirTalhao = permission === 'owner';
  const podeExcluirPlantio = (permission === 'owner' || permission === 'editor') && propertyActionAllowed(propriedade, 'propriedades', 'delete', true);
  
  // Estados para filtros
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroCultura, setFiltroCultura] = useState('todos');

  useEffect(() => {
    if (!confirmarExclusao.aberto) return undefined;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflowAnterior;
    };
  }, [confirmarExclusao.aberto]);

  useEffect(() => {
    if (!editTalhao) return undefined;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflowAnterior;
    };
  }, [editTalhao]);

  // Notificação quando algum talhão atinge a data de colheita hoje
  useEffect(() => {
    if (!talhoes.length) return;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const hojeStr = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}-${String(hoje.getDate()).padStart(2,'0')}`;
    const colhendoHoje = talhoes.filter(t => t.data_colheita === hojeStr);
    colhendoHoje.forEach(t => {
      toast.success(
        `🌾 ${t.nome} atingiu a data de colheita hoje!`,
        8000
      );
    });
  }, [talhoes]);

  // Buscar talhões do banco ao abrir
  useEffect(() => {
    async function fetchTalhoes() {
      if (operationalMode) {
        setTalhoes(initialTalhoes);
        return;
      }
      const CACHE_KEY = `climorax_talhoes_detail_${propriedade.id}`;

      // Carrega cache imediatamente para exibir dados offline
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) setTalhoes(JSON.parse(cached));
      } catch {}

      try {
        const { data, error } = await supabase
          .from('talhoes')
          .select(`
            *,
            propriedades!inner(
              municipio,
              estado,
              nome
            )
          `)
          .eq('propriedade_id', propriedade.id);
        if (!error) {
          const talhoesBase = data.map(t => ({
            ...t,
            propriedade_municipio: t.propriedades?.municipio,
            propriedade_estado: t.propriedades?.estado,
            propriedade_nome: t.propriedades?.nome
          }));
          let contextos = new Map();
          try {
            contextos = await buscarContextosCultivo(talhoesBase.map(t => t.id));
          } catch (cycleError) {
            console.warn('Ciclos de cultivo ainda indisponíveis; usando compatibilidade local.', cycleError);
          }
          const talhoesComPropriedade = talhoesBase.map(t => aplicarContextoCultivoAoTalhao(
            t,
            contextos.get(String(t.id)) || contextoCultivoLegado(t)
          ));
          setTalhoes(talhoesComPropriedade);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(talhoesComPropriedade)); } catch {}
        }
      } catch (err) {
        console.error('Erro ao buscar talhões:', err);
        // Mantém cache já carregado
      }
    }
    if (propriedade?.id) fetchTalhoes();
  }, [initialTalhoes, operationalMode, propriedade]);

  useEffect(() => {
    if ((!initialPlotId && !initialPlotName) || talhoes.length === 0) return;
    const talhaoAlvo = talhoes.find(item => (
      (initialPlotId && String(item.id) === String(initialPlotId))
      || (initialPlotName && item.nome === initialPlotName)
    ));
    if (talhaoAlvo) setTalhaoSelecionado(talhaoAlvo);
  }, [initialPlotId, initialPlotName, talhoes]);

  // Cadastro de talhão
  const handleCriar = async (novoTalhao) => {
    if (salvandoTalhao) return; // evita duplo envio
    if (!propertyActionAllowed(propriedade, 'propriedades', 'create', true)) { toast.error('Cadastro não autorizado nesta propriedade.'); return; }
    setSalvandoTalhao(true);
    try {
    // ⚠️ VERIFICAR LIMITE DO PLANO ANTES DE CRIAR
    const shared = Boolean(propriedade?._collaborationPermissions);
    const resultado = shared ? await checkSharedPlotCapacity(propriedade.id) : await verificarLimite('talhoes');
    
    if (!resultado.permitido) {
      if (shared) { toast.info('O plano do proprietário atingiu o limite ou está indisponível. Consulte o titular; seu plano pessoal não precisa ser alterado.'); return; }
      setLimiteInfo({ 
        usado: resultado.usado, 
        limite: resultado.limite 
      });
      setShowUpgradeModal(true);
      return; // PARA AQUI - Não cria!
    }
    
    // Talhões são áreas físicas permanentes: toda área cadastrada entra no limite.
    if (propriedade?.area) {
      const areaVigente = talhoes.reduce((sum, t) => sum + (Number(t.area) || 0), 0);
      const novaArea = Number(novoTalhao.area) || 0;
      const areaPropriedade = Number(propriedade.area);
      if (areaVigente + novaArea > areaPropriedade) {
        alert(
          `Área insuficiente na propriedade.\n\n` +
          `Já utilizado: ${areaVigente.toFixed(1)} ha\n` +
          `Este talhão: ${novaArea.toFixed(1)} ha\n` +
          `Total: ${(areaVigente + novaArea).toFixed(1)} ha\n\n` +
          `Limite da propriedade: ${areaPropriedade.toFixed(1)} ha`
        );
        return;
      }
    }

    const talhaoParaSalvar = {
      ...novoTalhao,
      propriedade_id: propriedade.id,
      user_id: shared ? propriedade.user_id : user.id,
      observacoes: novoTalhao.observacoes || '',
    };
    const { data, error } = await supabase
      .from('talhoes')
      .insert([talhaoParaSalvar])
      .select(`
        *,
        propriedades!inner(
          municipio,
          estado,
          nome,
          code_muni
        )
      `);
      
    if (error) throw error;
    if (data && data.length > 0) {
      // Processar dados da propriedade
      const novosComPropriedade = data.map(t => aplicarContextoCultivoAoTalhao({
        ...t,
        propriedade_municipio: t.propriedades?.municipio,
        propriedade_estado: t.propriedades?.estado,
        propriedade_nome: t.propriedades?.nome
      }, { cicloAtual: null, ultimoCiclo: null }));
      
      setTalhoes([...talhoes, ...novosComPropriedade]);
      setAba('listar'); // Vai para a aba de listagem após cadastrar
    }
    } catch (error) {
      toast.error(error?.message || 'Não foi possível cadastrar o talhão.');
    } finally {
      setSalvandoTalhao(false);
    }
  };

  const atualizarColecaoTalhoes = transform => {
    const next = transform(talhoes);
    setTalhoes(next);
    onTalhoesChange?.(next);
  };

  const handleExcluir = async () => {
    const { tipo, id, talhaoId } = confirmarExclusao;
    if (!id || excluindo) return;
    setExcluindo(true);
    setConfirmarExclusao(current => ({ ...current, erro: '' }));
    try {
      if (tipo === 'plantio-operacional') {
        await deleteOperationalPlanting(id, propriedade.id);
        atualizarColecaoTalhoes(current => current.map(item => (
          String(item.id) === String(talhaoId)
            ? removerPlantioDoTalhaoOperacional(item, id)
            : item
        )));
        setTalhaoSelecionado(current => (current && String(current.id) === String(talhaoId)
          ? removerPlantioDoTalhaoOperacional(current, id)
          : current));
        toast.success('Plantio excluído. O talhão foi mantido.');
      } else if (operationalMode) {
        await deleteOperationalPlot(id, propriedade.id);
        atualizarColecaoTalhoes(current => current.filter(item => String(item.id) !== String(id)));
        toast.success('Talhão vazio excluído com segurança.');
      } else {
        await deleteEmptyLegacyPlot(id);
        atualizarColecaoTalhoes(current => current.filter(item => String(item.id) !== String(id)));
        toast.success('Talhão vazio excluído com segurança.');
      }
      fecharConfirmacao();
    } catch (error) {
      const rawMessage = String(error?.message || '');
      const backendIndisponivel = /excluir_plantio_operacional|function.*does not exist|edge function|non-2xx/i.test(rawMessage);
      setConfirmarExclusao(current => ({
        ...current,
        erro: backendIndisponivel
          ? 'A exclusão ainda não está disponível neste ambiente. A atualização do banco e da função de exclusão precisa ser publicada.'
          : rawMessage || 'Não foi possível concluir a exclusão.',
      }));
    } finally {
      setExcluindo(false);
    }
  };

  function pedirConfirmacaoExclusao(talhao) {
    const bloqueio = motivoBloqueioExclusaoTalhao(talhao);
    if (bloqueio) {
      toast.error(bloqueio);
      return;
    }
    setConfirmarExclusao({ aberto: true, tipo: 'talhao', id: talhao.id, nome: talhao.nome, talhaoId: talhao.id, erro: '' });
  }

  function pedirConfirmacaoExclusaoPlantio(talhao, plantio) {
    setConfirmarExclusao({
      aberto: true,
      tipo: 'plantio-operacional',
      id: plantio.id,
      nome: plantio.nome_legado || plantio.cultura || 'Plantio',
      talhaoId: talhao.id,
      erro: '',
    });
  }

  // Quem entrou vindo da Central de Operações volta direto para lá (mesma
  // fazenda e visão de mapa) em vez de ficar preso nesta aba.
  function voltarParaOrigem() {
    if (returnTarget && onNavigate) {
      onNavigate(returnTarget, returnContext || {});
      return true;
    }
    return false;
  }

  function fecharConfirmacao() {
    setConfirmarExclusao({ aberto: false, tipo: null, id: null, nome: '', talhaoId: null, erro: '' });
  }

  // Iniciar edição
  const iniciarEdicao = (talhao) => setEditTalhao(talhao);

  // Cancelar edição
  const cancelarEdicao = () => setEditTalhao(null);

  // Salvar edição
  const handleSalvarEdicao = async (talhaoEditado) => {
    if (salvandoTalhao) return; // evita duplo envio
    setSalvandoTalhao(true);
    try {
    const { id, nome, observacoes } = talhaoEditado;

    if (talhaoEditado._operationalPhysical) {
      try {
        await updateOperationalPlot({
          plotId: talhaoEditado._physicalPlotId ?? id,
          propertyId: propriedade.id,
          name: nome,
        });
      } catch (operationalError) {
        alert(`Erro ao salvar: ${operationalError?.message || 'erro inesperado'}`);
        return;
      }
      const anterior = talhoes.find(t => String(t.id) === String(id));
      const talhaoAtualizado = anterior ? { ...anterior, nome } : { ...talhaoEditado, nome };
      setTalhoes(talhoes.map(t => (String(t.id) === String(id) ? talhaoAtualizado : t)));
      onTalhoesChange?.(talhoes.map(t => (String(t.id) === String(id) ? talhaoAtualizado : t)));
      setEditTalhao(null);
      return;
    }

    const talhaoParaAtualizar = {
      nome,
      observacoes,
    };
    
    const { data, error } = await supabase
      .from('talhoes')
      .update(talhaoParaAtualizar)
      .eq('id', id)
      .select(`
        *,
        propriedades!inner(
          municipio,
          estado,
          nome,
          code_muni
        )
      `);
    
    if (error) {
      console.error('Erro ao atualizar talhão:', error);
      alert(`Erro ao salvar: ${error.message}`);
      return;
    }
    
    if (!data || data.length === 0) {
      alert('Não foi possível salvar: o talhão não foi encontrado ou você não tem permissão para alterá-lo.');
      return;
    }

    // Processar dados da propriedade como no fetchTalhoes
    const talhaoAtualizadoBase = {
      ...data[0],
      propriedade_municipio: data[0].propriedades?.municipio,
      propriedade_estado: data[0].propriedades?.estado,
      propriedade_nome: data[0].propriedades?.nome
    };
    const anterior = talhoes.find(t => t.id === id);
    const talhaoAtualizado = aplicarContextoCultivoAoTalhao(talhaoAtualizadoBase, {
      cicloAtual: anterior?.ciclo_atual || null,
      ultimoCiclo: anterior?.ultimo_ciclo || null,
      ciclos: anterior?.ciclos_cultivo,
    });

    setTalhoes(talhoes.map(t => (t.id === id ? talhaoAtualizado : t)));
    setEditTalhao(null);
    } finally {
      setSalvandoTalhao(false);
    }
  };

  // O status agronômico pertence ao ciclo; o talhão físico nunca deixa de existir.
  function isColhido(t) {
    return !t?.ciclo_atual && t?.ultimo_ciclo?.status === 'colhido';
  }

  const atualizarTalhaoComCiclo = ({ type, ciclo, contexto: contextoExclusao, talhaoId: cicloTalhaoId }) => {
    const talhaoId = String(cicloTalhaoId || ciclo?.talhao_id || gestaoCultivoTalhao?.id);
    if (gestaoCultivoTalhao?._operationalPhysical) {
      const atualizarPlantios = item => {
        if (String(item.id) !== talhaoId && String(item._physicalPlotId) !== talhaoId) return item;
        const anteriores = (item.plantios || []).filter(plantio => String(plantio.id) !== String(ciclo?.id));
        const plantios = type === 'deleted' ? anteriores : [ciclo, ...anteriores];
        const cicloAtual = type === 'harvested' || type === 'deleted' ? null : ciclo;
        return { ...item, plantios, ciclo_atual: cicloAtual, ultimo_ciclo: ciclo || anteriores[0] || null, temHistorico: plantios.length > 0 };
      };
      atualizarColecaoTalhoes(current => current.map(atualizarPlantios));
      setTalhaoSelecionado(current => current ? atualizarPlantios(current) : current);
      setGestaoCultivoTalhao(null);
      toast.success(type === 'harvested'
        ? 'Colheita registrada. O talhão está disponível para um novo plantio.'
        : type === 'updated'
          ? 'Plantio atualizado. As novas informações já estão disponíveis em todo o sistema.'
          : 'Plantio cadastrado e vinculado ao talhão.');
      return;
    }
    const contexto = type === 'deleted'
      ? (contextoExclusao || { cicloAtual: null, ultimoCiclo: null })
      : type === 'harvested'
        ? { cicloAtual: null, ultimoCiclo: ciclo }
        : { cicloAtual: ciclo, ultimoCiclo: ciclo };
    atualizarColecaoTalhoes(current => current.map(item => String(item.id) === talhaoId
      ? aplicarContextoCultivoAoTalhao(item, contexto)
      : item));
    setTalhaoSelecionado(current => current && String(current.id) === talhaoId
      ? aplicarContextoCultivoAoTalhao(current, contexto)
      : current);
    setGestaoCultivoTalhao(null);
    toast.success(type === 'deleted'
      ? 'Plantio excluído. O talhão foi mantido e está disponível.'
      : type === 'harvested'
        ? 'Colheita registrada. O talhão está disponível para um novo plantio.'
        : type === 'updated'
          ? 'Plantio atualizado. As novas informações já estão disponíveis em todo o sistema.'
          : 'Plantio cadastrado e vinculado ao talhão.');
  };

  const abrirGestaoCultivo = (talhao, modo) => {
    setGestaoCultivoTalhao(talhao);
    setGestaoCultivoModo(modo);
  };

  const modalCultivo = <GestaoCultivoModal
    open={Boolean(gestaoCultivoTalhao)}
    onClose={() => setGestaoCultivoTalhao(null)}
    talhao={gestaoCultivoTalhao}
    cicloAtual={gestaoCultivoTalhao?.ciclo_atual || null}
    user={user}
    mode={gestaoCultivoModo}
    onSaved={atualizarTalhaoComCiclo}
    allowDelete={podeExcluirPlantio}
    darkMode={darkMode}
  />;

  if (talhaoSelecionado && !confirmarExclusao.aberto) {
    return <>
      <TalhaoPlantios
        key={talhaoSelecionado.id}
        talhao={talhaoSelecionado}
        propriedade={propriedade}
        user={user}
        permission={permission}
        darkMode={darkMode}
        onVoltar={() => { if (!voltarParaOrigem()) setTalhaoSelecionado(null); }}
        onCadastrar={podeEditar ? () => abrirGestaoCultivo(talhaoSelecionado, 'create') : undefined}
        onExcluirPlantio={operationalMode && podeExcluirPlantio
          ? plantio => pedirConfirmacaoExclusaoPlantio(talhaoSelecionado, plantio)
          : undefined}
        initialSection={initialSection}
      />
      {modalCultivo}
    </>;
  }

  return (
    <div
      className={`props-workspace props-plots-workspace ${darkMode ? 'is-dark' : ''}`}
      style={{
        maxWidth: 1200,
        margin: '0 auto',
        background: darkMode ? '#1a1a2e' : '#f7fafd',
        borderRadius: 16,
        boxShadow: darkMode ? '0 2px 8px #0006' : '0 2px 16px #0001',
        padding: 16,
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
        paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 16px)',
        paddingRight: 'calc(env(safe-area-inset-right, 0px) + 16px)',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
      }}
    >
      <div className="props-heading-row props-plots-heading" style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <button
          type="button"
          className="props-back-button"
          onClick={() => { if (!voltarParaOrigem()) onVoltar(); }}
          aria-label={`Voltar para propriedades; sair dos talhões de ${propriedade.nome}`}
          title="Voltar para propriedades"
          style={{
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '0.5rem 1.2rem',
            fontWeight: 600,
            fontSize: 16,
            cursor: 'pointer',
            marginRight: 12,
            boxShadow: '0 2px 8px #1976d233'
          }}
        >
          <FaArrowLeft aria-hidden="true" />
        </button>
        <div className="props-plots-heading-icon"><FaMapMarkedAlt aria-hidden="true" /></div>
        <div className="props-plots-heading-copy">
          <span>Gestão da propriedade</span>
          <h1>Talhões de <strong>{propriedade.nome}</strong></h1>
          <p>{propriedade.municipio}{propriedade.estado ? ` · ${propriedade.estado}` : ''}{propriedade.area ? ` · ${propriedade.area} ha` : ''}</p>
        </div>
      </div>

      {/* Abas */}
      <div className="props-tabbar" style={{ display: 'flex', marginBottom: 16 }}>
        <button
          className={`props-tab-btn ${aba === 'listar' ? 'is-active' : ''}`}
          onClick={() => setAba('listar')}
          style={{
            flex: 1,
            border: 'none',
            borderRadius: '8px',
            padding: '0.7rem 0',
            fontWeight: 700,
            fontSize: 16,
            cursor: 'pointer'
          }}
        >
          <FaLayerGroup aria-hidden="true" /> Talhões cadastrados
        </button>
      </div>

      {aba === 'listar' && (
        <div>
          {/* Dashboard Resumido */}
          {talhoes.length > 0 && (() => {
            const lifecycleSummary = summarizeCropLifecycles(talhoes);
            const talhoesAtuais = talhoes.filter(t => {
              const lifecycle = summarizeCropLifecycles([t]);
              return lifecycle.active === 1;
            });
            const totalArea = talhoes.reduce((sum, t) => sum + (Number(t.area) || 0), 0);
            const disponiveis = lifecycleSummary.available + lifecycleSummary.harvested;
            const planejados = lifecycleSummary.planned;
            const emDesenvolvimento = lifecycleSummary.active;
            const culturas = [...new Set(talhoesAtuais.map(t => t.ciclo_atual?.cultura).filter(Boolean))];
            
            return (
              <section className="props-plots-summary" style={{
                background: darkMode ? '#232526' : '#fff',
                borderRadius: 10,
                padding: '16px 14px',
                marginBottom: 14,
                boxShadow: darkMode ? '0 2px 8px #0006' : '0 2px 8px rgba(0,0,0,0.06)',
                border: `1px solid ${darkMode ? '#444' : '#e8f0f8'}`
              }}>
                <div className="props-plots-summary-label" style={{
                  fontSize: 11, 
                  fontWeight: 700, 
                  color: darkMode ? '#90caf9' : '#1976d2',
                  marginBottom: 12,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  <FaLeaf aria-hidden="true" /> Resumo da propriedade
                </div>

                <div className="props-plots-summary-main" style={{
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12
                }}>
                  <div className="props-plots-summary-stat">
                    <span><FaSeedling aria-hidden="true" /></span>
                    <div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: darkMode ? '#90caf9' : '#1976d2', lineHeight: 1 }}>
                      {emDesenvolvimento}
                    </div>
                    <div style={{ fontSize: 12, color: darkMode ? '#aaa' : '#666', marginTop: 4 }}>
                      {emDesenvolvimento === 1 ? 'talhão em cultivo' : 'talhões em cultivo'}
                    </div>
                    </div>
                  </div>
                  
                  <div className="props-plots-summary-stat">
                    <span><FaRulerCombined aria-hidden="true" /></span>
                    <div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: darkMode ? '#90caf9' : '#1976d2', lineHeight: 1 }}>
                      {totalArea.toFixed(1)}
                    </div>
                    <div style={{ fontSize: 12, color: darkMode ? '#aaa' : '#666', marginTop: 4 }}>
                      hectares mapeados
                    </div>
                    </div>
                  </div>
                </div>

                <div className="props-plots-status-row" style={{
                  display: 'flex', 
                  gap: 10,
                  paddingTop: 14,
                  borderTop: '1px solid #f0f4f8'
                }}>
                  <div style={{ 
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#4caf50'
                    }}></div>
                    <div style={{ fontSize: 13, color: darkMode ? '#e0e0e0' : '#555' }}>
                      <b>{disponiveis}</b> disponíveis
                    </div>
                  </div>
                  
                  <div style={{ 
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#ff9800'
                    }}></div>
                    <div style={{ fontSize: 13, color: darkMode ? '#e0e0e0' : '#555' }}>
                      <b>{emDesenvolvimento}</b> ativos
                    </div>
                  </div>
                  {planejados > 0 && (
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#1976d2'
                      }}></div>
                      <div style={{ fontSize: 13, color: darkMode ? '#e0e0e0' : '#555' }}>
                        <b>{planejados}</b> planejado{planejados === 1 ? '' : 's'}
                      </div>
                    </div>
                  )}
                </div>

                {culturas.length > 0 && (
                  <div className="props-plots-cultures" style={{
                    marginTop: 14,
                    paddingTop: 14,
                    borderTop: '1px solid #f0f4f8',
                    display: 'flex',
                    gap: 6,
                    flexWrap: 'wrap'
                  }}>
                    {culturas.map(c => (
                      <span key={c} style={{
                        background: darkMode ? '#1e3a5f' : '#e3f2fd',
                        color: darkMode ? '#90caf9' : '#1976d2',
                        fontSize: 11,
                        padding: '4px 10px',
                        borderRadius: 12,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.3
                      }}>
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </section>
            );
          })()}

          {/* Filtros */}
          {talhoes.length > 0 && (
            <div className="props-plots-filters" style={{ marginBottom: 16 }}>
              {/* Busca */}
              <label className="props-search-field props-plots-search">
                <FaSearch aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Buscar área por nome, cultivo ou observação"
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                />
              </label>
              
              {/* Filtros de Status e Cultura */}
              <div className="props-plots-filter-row" style={{ display: 'flex', gap: 8, width: '100%' }}>
                <label><FaFilter aria-hidden="true" />
                <select
                  aria-label="Filtrar talhões por situação"
                  value={filtroStatus}
                  onChange={e => setFiltroStatus(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1.5px solid ${darkMode ? '#444' : '#e0e7ef'}`,
                    fontSize: 14,
                    fontWeight: 600,
                    color: darkMode ? '#90caf9' : '#1976d2',
                    cursor: 'pointer',
                    boxShadow: darkMode ? '0 2px 8px #0006' : '0 1px 3px rgba(0,0,0,0.04)',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    background: darkMode ? '#2a2a2a' : '#fff'
                  }}
                >
                  <option value="todos">Todos os talhões</option>
                  <option value="disponivel">Sem plantio ativo</option>
                  <option value="desenvolvimento">Com plantio ativo</option>
                  <option value="planejado">Plantio planejado</option>
                  <option value="colhido">Com último ciclo colhido</option>
                </select>
                </label>
                <label><FaLeaf aria-hidden="true" />
                <select
                  aria-label="Filtrar talhões por cultura"
                  value={filtroCultura}
                  onChange={e => setFiltroCultura(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1.5px solid ${darkMode ? '#444' : '#e0e7ef'}`,
                    fontSize: 14,
                    fontWeight: 600,
                    color: darkMode ? '#90caf9' : '#1976d2',
                    cursor: 'pointer',
                    boxShadow: darkMode ? '0 2px 8px #0006' : '0 1px 3px rgba(0,0,0,0.04)',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    background: darkMode ? '#2a2a2a' : '#fff'
                  }}
                >
                  <option value="todos">Todas as culturas</option>
                  {[...new Set(talhoes.map(t => t.ciclo_atual?.cultura?.trim()).filter(Boolean).map(c => c.charAt(0).toUpperCase() + c.slice(1).toLowerCase()))].map(c => (
                    <option key={c} value={c}>
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </option>
                  ))}
                </select>
                </label>
              </div>
            </div>
          )}

          {talhoes.length === 0 ? (
            <div style={{ color: darkMode ? '#aaa' : '#68798a', textAlign: 'center', marginTop: 24, padding: 28 }}>
              <strong style={{ display: 'block', fontSize: 18, marginBottom: 7 }}>Nenhum talhão mapeado</strong>
              <span style={{ display: 'block', marginBottom: 14 }}>Crie o talhão desenhando seu perímetro na Central de Operações; a área será calculada automaticamente.</span>
              {onNavigate && <button type="button" className="props-form-submit" onClick={() => onNavigate('sensoriamento')}>Abrir Central de Operações</button>}
            </div>
          ) : (
            <div className="props-talhoes-grid" style={{ 
              display: 'grid',
              gap: 14
            }}>
              {[...talhoes]
                .filter(t => {
                  // Filtro de busca
                  if (busca) {
                    const termo = busca.toLowerCase();
                    const nome = (t.nome || '').toLowerCase();
                    const cultura = (t.ciclo_atual?.cultura || t.ultimo_ciclo?.cultura || '').toLowerCase();
                    const obs = (t.observacoes || '').toLowerCase();
                    if (!nome.includes(termo) && !cultura.includes(termo) && !obs.includes(termo)) {
                      return false;
                    }
                  }
                  
                  // Filtro de status
                  if (filtroStatus === 'colhido' && !isColhido(t)) return false;
                  if (filtroStatus === 'disponivel' && t.ciclo_atual) return false;
                  if (filtroStatus === 'desenvolvimento' && !t.ciclo_atual) return false;
                  if (filtroStatus === 'planejado' && t.ciclo_atual?.status !== 'planejado') return false;
                  
                  // Filtro de cultura
                  if (filtroCultura !== 'todos') {
                    const culturaAtual = t.ciclo_atual?.cultura || '';
                    const cultNorm = culturaAtual.trim().charAt(0).toUpperCase() + culturaAtual.trim().slice(1).toLowerCase();
                    if (cultNorm !== filtroCultura) return false;
                  }
                  
                  return true;
                })
                .sort((a, b) => {
                  const dataA = a.ciclo_atual?.data_plantio || a.ultimo_ciclo?.data_plantio || '';
                  const dataB = b.ciclo_atual?.data_plantio || b.ultimo_ciclo?.data_plantio || '';
                  return String(dataB).localeCompare(String(dataA));
                })
                .map(t => {
                  const diasDesde = t.data_plantio ? (() => {
                    const hoje = new Date(); hoje.setHours(0,0,0,0);
                    const [y,m,d] = t.data_plantio.split('-').map(Number);
                    return Math.floor((hoje - new Date(y, m-1, d)) / 86400000);
                  })() : null;
                  const plantioFuturo = diasDesde !== null && diasDesde < 0;
                  const totalPlantios = Array.isArray(t.plantios) ? t.plantios.length : 0;
                  const plantingView = getPlotPlantingView(t);
                  const estadoVisual = t.ciclo_atual
                    ? (plantioFuturo || t.ciclo_atual.status === 'planejado' ? 'planned' : 'active')
                    : 'available';
                  const rotuloEstado = estadoVisual === 'planned'
                    ? 'Plantio planejado'
                    : estadoVisual === 'active' ? 'Em cultivo' : 'Área disponível';

                  return (
                <article
                  className={`props-plot-card is-${estadoVisual}`}
                  key={t.id}
                  onClick={() => {
                    if (!confirmarExclusao.aberto) setTalhaoSelecionado(t);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <header className="props-plot-card-header">
                    <span className={`props-plot-card-icon is-${estadoVisual}`} aria-hidden="true">
                      <FaSeedling />
                    </span>
                    <div className="props-plot-card-title">
                      <h3>{t.nome}</h3>
                      <span className={`props-plot-status is-${estadoVisual}`}>
                        <i aria-hidden="true" />
                        {rotuloEstado}
                      </span>
                    </div>
                    {t.ciclo_atual?.cultura && (
                      <span className="props-plot-culture">
                        {t.ciclo_atual.cultura.toUpperCase()}
                      </span>
                    )}
                  </header>

                  <div className="props-plot-facts">
                    <div className="props-plot-fact">
                      <span>Área mapeada</span>
                      <strong>{t.area} <small>ha</small></strong>
                    </div>
                    
                    {diasDesde !== null && t.ciclo_atual && (
                      <div className={`props-plot-fact ${plantioFuturo ? 'is-warning' : ''}`}>
                        <span>{plantioFuturo ? 'Plantio em' : 'Tempo de cultivo'}</span>
                        <strong>
                          {plantioFuturo
                            ? `${Math.abs(diasDesde)} dias`
                            : diasDesde === 0 ? 'Hoje' : diasDesde === 1 ? '1 dia' : `${diasDesde} dias`
                          }
                        </strong>
                      </div>
                    )}
                    
                    {isColhido(t) && (
                      <div className="props-plot-fact is-success">
                        <span>Última colheita</span>
                        <strong>
                          {t.ultimo_ciclo.data_colheita.split('-').reverse().join('/')}
                        </strong>
                      </div>
                    )}
                    
                    {t.ciclo_atual?.data_plantio && (
                      <div className="props-plot-fact">
                        <span>Data do plantio</span>
                        <strong>
                          {t.ciclo_atual.data_plantio.split('-').reverse().join('/')}
                        </strong>
                      </div>
                    )}
                    {t.ciclo_atual?.data_colheita_prevista && (
                      <div className="props-plot-fact is-success">
                        <span>Colheita prevista</span>
                        <strong>
                          {t.ciclo_atual.data_colheita_prevista.split('-').reverse().join('/')}
                        </strong>
                      </div>
                    )}
                  </div>

                  {!plantingView.current && plantingView.history.length === 0 && !plantingView.historyUnavailable && (
                    <div className="props-plot-empty-history">
                      <FaLeaf aria-hidden="true" />
                      <span><strong>Nenhum plantio registrado</strong>Área pronta para um novo ciclo.</span>
                    </div>
                  )}

                  {onNavigate && pecuariaPlanting(t) && (
                    <button type="button" className="props-plot-action is-secondary"
                      onClick={event => {
                        event.stopPropagation();
                        onNavigate('pecuaria', { propertyId: propriedade.id, plotId: t.id });
                      }}>
                      Abrir na Pecuária
                      <FaChevronRight aria-hidden="true" />
                    </button>
                  )}

                  <button type="button" className="props-plot-action is-secondary"
                    onClick={event => { event.stopPropagation(); setTalhaoSelecionado(t); }}>
                    {plantingView.current
                      ? plantingView.current.status === 'planejado' ? 'Ver planejamento' : 'Abrir plantio'
                      : plantingView.history.length ? `Ver histórico (${plantingView.history.length})` : 'Ver talhão'}
                    <FaChevronRight aria-hidden="true" />
                  </button>

                  {operationalMode && totalPlantios > 0 && (
                    <section className="props-plot-history" aria-label={`Histórico de plantios de ${t.nome}`}>
                      <div className="props-plot-history-heading">
                        <FaHistory aria-hidden="true" />
                        <strong>Histórico de plantios</strong>
                        <span>{totalPlantios}</span>
                      </div>
                      <div className="props-plot-history-list">
                        {t.plantios.map(planting => {
                          const legacyDetail = operationalPlantingToLegacyPlot(t, planting, propriedade);
                          return (
                            <div className="props-plot-history-row" key={planting.id}>
                              <button
                                type="button"
                                className="props-plot-history-item"
                                disabled={!legacyDetail}
                                title={legacyDetail
                                  ? 'Abrir fotos, fenologia e informações deste plantio'
                                  : 'As informações detalhadas deste plantio ainda não estão disponíveis'}
                                onClick={event => {
                                  event.stopPropagation();
                                  if (legacyDetail) setTalhaoSelecionado(legacyDetail);
                                }}
                              >
                                <span className="props-plot-history-icon"><FaSeedling aria-hidden="true" /></span>
                                <span className="props-plot-history-copy">
                                  <strong>{planting.nome_legado || planting.cultura || 'Plantio histórico'}</strong>
                                  <small>
                                    {formatHistoricalDate(planting.data_plantio)}
                                    {planting.data_colheita ? ` até ${formatHistoricalDate(planting.data_colheita)}` : ''}
                                  </small>
                                </span>
                                <span className="props-plot-history-action">
                                  {legacyDetail ? 'Abrir detalhes' : 'Vínculo pendente'}
                                  {legacyDetail && <FaChevronRight aria-hidden="true" />}
                                </span>
                              </button>
                              {podeExcluirPlantio && (
                                <button
                                  type="button"
                                  className="props-plot-history-delete"
                                  title={`Excluir o plantio ${planting.nome_legado || planting.cultura || ''}`.trim()}
                                  aria-label={`Excluir plantio ${planting.nome_legado || planting.cultura || ''}`.trim()}
                                  onClick={event => {
                                    event.stopPropagation();
                                    pedirConfirmacaoExclusaoPlantio(t, planting);
                                  }}
                                >
                                  <FaTrash aria-hidden="true" />
                                  <span>Excluir</span>
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  {/* Observações: apenas se houver */}
                  {t.observacoes && (
                    <div style={{ 
                      fontSize: 12,
                      color: darkMode ? '#aaa' : '#777',
                      fontStyle: 'italic',
                      marginBottom: 8,
                      padding: '6px 8px',
                      background: darkMode ? '#2a2a2a' : '#f9fafb',
                      borderRadius: 6,
                      borderLeft: `3px solid ${darkMode ? '#444' : '#e0e7ef'}`
                    }}>
                      {t.observacoes}
                    </div>
                  )}

                  {/* Botões de Ação */}
                  {(podeEditar || podeExcluirTalhao) && (
                    <div className="props-plot-actions">
                      {podeEditar && t.ciclo_atual && (
                        <button
                          type="button"
                          className="props-plot-action is-secondary"
                          onClick={event => { event.stopPropagation(); abrirGestaoCultivo(t, 'edit'); }}
                        >
                          ✎ Editar plantio
                        </button>
                      )}
                      {podeExcluirPlantio && !t.ciclo_atual && t.ultimo_ciclo && !String(t.ultimo_ciclo.id || '').startsWith('legado-') && (
                        <button
                          type="button"
                          className="props-plot-action is-danger"
                          onClick={event => { event.stopPropagation(); abrirGestaoCultivo({ ...t, ciclo_atual: t.ultimo_ciclo }, 'delete'); }}
                        >
                          <FaTrash aria-hidden="true" /> Excluir último plantio
                        </button>
                      )}
                      {podeEditar && <button
                        type="button"
                        className={`props-plot-action ${t.ciclo_atual ? 'is-warning' : 'is-primary'}`}
                        onClick={event => { event.stopPropagation(); abrirGestaoCultivo(t, t.ciclo_atual ? 'harvest' : 'create'); }}
                      >
                        {t.ciclo_atual ? '🌾 Registrar colheita' : '🌱 Cadastrar plantio'}
                      </button>}
                      {podeEditar && <button className="props-plot-edit"
                        type="button"
                        onClick={e => { e.stopPropagation(); iniciarEdicao(t); }}
                      >
                        <FaEdit aria-hidden="true" /> Editar talhão
                      </button>}
                      {podeExcluirTalhao && <button className="props-plot-delete"
                        type="button"
                        disabled={Boolean(motivoBloqueioExclusaoTalhao(t))}
                        title={motivoBloqueioExclusaoTalhao(t) || 'Excluir talhão vazio'}
                        onClick={e => { e.stopPropagation(); pedirConfirmacaoExclusao(t); }}
                      >
                        <FaTrash aria-hidden="true" /> Excluir talhão
                      </button>}
                      {podeExcluirTalhao && motivoBloqueioExclusaoTalhao(t) && (
                        <small className="props-plot-protection-note">
                          Exclua o plantio primeiro. Talhões com qualquer histórico permanecem protegidos.
                        </small>
                      )}
                    </div>
                  )}
                </article>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* Confirmação de exclusão - fora do map, global à tela */}
      {confirmarExclusao.aberto && typeof document !== 'undefined' && createPortal(
        <div className={`props-confirm-overlay ${darkMode ? 'is-dark' : ''}`}>
          <div
            className="props-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="props-confirm-title"
            aria-describedby="props-confirm-description"
          >
            <h3 id="props-confirm-title">
              {confirmarExclusao.tipo === 'plantio-operacional' ? 'Excluir plantio' : 'Excluir talhão vazio'}
            </h3>
            <p id="props-confirm-description">
              {confirmarExclusao.tipo === 'plantio-operacional' ? (
                <>Tem certeza que deseja excluir o plantio <strong>{confirmarExclusao.nome}</strong>? O talhão será mantido e poderá receber um novo plantio.</>
              ) : (
                <>Tem certeza que deseja excluir o talhão vazio <strong>{confirmarExclusao.nome}</strong>? A operação será bloqueada se o banco encontrar qualquer plantio ou histórico vinculado.</>
              )}
            </p>
            {confirmarExclusao.erro && (
              <p role="alert" className="props-confirm-error">
                {confirmarExclusao.erro}
              </p>
            )}
            <div className="props-confirm-actions">
              <button
                type="button"
                className="props-confirm-delete"
                onClick={handleExcluir}
                disabled={excluindo}
              >
                {excluindo ? 'Excluindo…' : confirmarExclusao.tipo === 'plantio-operacional' ? 'Excluir plantio' : 'Excluir talhão'}
              </button>
              <button
                type="button"
                className="props-confirm-cancel"
                onClick={() => {
                  fecharConfirmacao();
                }}
                disabled={excluindo}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de edição */}
      {editTalhao && typeof document !== 'undefined' && createPortal(
        <div className={`props-edit-plot-overlay ${darkMode ? 'is-dark' : ''}`} role="presentation" onMouseDown={event => {
          if (event.target === event.currentTarget && !salvandoTalhao) cancelarEdicao();
        }}>
          <div className="props-edit-plot-dialog" role="dialog" aria-modal="true" aria-label="Editar talhão">
            <CadastroTalhao
              talhaoEdicao={editTalhao}
              onSalvarEdicao={handleSalvarEdicao}
              onCriar={() => {}}
              propriedadeSelecionada={propriedade}
              salvando={salvandoTalhao}
            />
            <button
              type="button"
              className="props-edit-plot-cancel"
              onClick={cancelarEdicao}
              disabled={salvandoTalhao}
            >
              Cancelar
            </button>
          </div>
        </div>,
        document.body
      )}

      {modalCultivo}

      {/* Modal de Upgrade de Plano */}
      <UpgradeModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        recurso="talhoes"
        planoAtual={plano?.tipo}
        limiteInfo={limiteInfo}
        usuario={user}
      />
    </div>
  );
}
